var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import { isValidElementType } from 'react-is';
import { hasValue, loadAllScriptsByModuleId, lookupComponent, getModuleIdFromWidget, isUserAllowed } from "@utils";
import { NotAuthorized } from "@core/layout/components/ErrorPages";
import { UXPContext } from "@core/context/Context";
import { LoadingSpinner, DefaultLoader } from "@widget/loaders";
import { shouldShowPlaceholder, hasConfigOptions, getMergedWidgetProps } from "@core/dashboard/dashboardUtils";
import { useAddOns } from "@helper/AddOn/useAddOns";
import InstallAddOn from "@helper/AddOn/InstallAddOn";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import ButtonComponent from '@widget/buttons/ButtonComponent';
const ConfigPlaceholder = ({ name, message, onConfigure }) => {
    const context = useContext(UXPContext);
    return (_jsxs("div", { className: "uxpcore_componentrenderer__config-placeholder", children: [_jsx("p", { className: "uxpcore_componentrenderer__config-placeholder-name", children: name }), _jsx("p", { className: "uxpcore_componentrenderer__config-placeholder-message", children: message || context.$L('uxp-core.text.need-to-configure-widget') }), onConfigure && (_jsx(ButtonComponent, { title: context.$L('uxp-core.text.configure'), onClick: onConfigure, leftIcon: "fas cog" }))] }));
};
function extractDefaultPropsFromSchema(instance) {
    var _a;
    return (((_a = instance.configs) === null || _a === void 0 ? void 0 : _a.props) || [])
        .filter((p) => 'name' in p && p.value !== undefined)
        .reduce((acc, p) => {
        acc[p.name] = p.value;
        return acc;
    }, {});
}
const ComponentRendererBase = ({ moduleId, componentId, componentInstance, configuredProps, hasConfigured, onConfigureRequest, configNeededMessage, additionalProps, notFoundMessage, isPreview = false, onAddonInstalled, forceConfigView = false, }) => {
    var _a, _b, _c;
    const context = useContext(UXPContext);
    const addOns = useAddOns();
    const [resolvedInstance, setResolvedInstance] = useState(componentInstance !== null && componentInstance !== void 0 ? componentInstance : null);
    const [loading, setLoading] = useState(false);
    const [installInstance, setInstallInstance] = useState(null);
    // Path A: resolve component by moduleId/componentId with lazy script loading
    const loadComponent = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!hasValue(moduleId) || !hasValue(componentId))
            return;
        setLoading(true);
        const id = `${moduleId}/${componentId}`.toLowerCase();
        let reg = lookupComponent(id);
        if (!reg) {
            yield loadAllScriptsByModuleId(context, moduleId);
            reg = lookupComponent(id);
        }
        if (!reg) {
            console.error('ComponentRenderer: component not found after loading script', moduleId, componentId);
            setResolvedInstance(null);
            setLoading(false);
            return;
        }
        const schemaDefaults = extractDefaultPropsFromSchema({ configs: reg.configs });
        const instance = {
            _id: id,
            id: reg.id,
            key: id,
            name: reg.name || componentId,
            component: reg.component,
            configs: reg.configs,
            defaultProps: (_a = reg.defaultProps) !== null && _a !== void 0 ? _a : schemaDefaults,
            configuredProps: configuredProps !== null && configuredProps !== void 0 ? configuredProps : {},
            hasConfigured: hasConfigured !== null && hasConfigured !== void 0 ? hasConfigured : Object.keys(configuredProps || {}).length > 0,
            installed: reg.installed,
            usecaseId: reg.usecaseId,
            usecaseName: reg.usecaseName,
        };
        setResolvedInstance(instance);
        setLoading(false);
    }), [moduleId, componentId, context, configuredProps, hasConfigured]);
    useEffect(() => {
        if (componentInstance != null) {
            setResolvedInstance(componentInstance);
        }
        else {
            loadComponent();
        }
    }, [componentInstance, loadComponent]);
    if (loading) {
        return _jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(LoadingSpinner, {}) });
    }
    if (!resolvedInstance) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__not-found", children: notFoundMessage || context.$L('uxp-core.text.unable-to-find-the-view') }));
    }
    // Component-level permission gate — the live registry is the source of truth
    // (persisted dashboard JSON could carry stale role fields)
    const registeredDef = lookupComponent(resolvedInstance.id);
    if (registeredDef && !isUserAllowed(context, registeredDef.userGroups || [], registeredDef.appRoles || [])) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(NotAuthorized, { action: componentInstance != null ? _jsx(_Fragment, {}) : undefined }) }));
    }
    // Addon check — show install/configure placeholder for uninstalled/unconfigured addon components
    const addonModuleId = getModuleIdFromWidget(resolvedInstance);
    const isFromAddOn = addOns.isAddOn(addonModuleId);
    if (isFromAddOn) {
        const { installed, configured } = addOns.isAddOnInstalledAndConfigured(addonModuleId);
        const needToConfigure = addOns.isConfigurationRequired(addonModuleId);
        const showAddonPlaceholder = !installed || (!configured && needToConfigure);
        if (showAddonPlaceholder) {
            const addOnName = addOns.getAddOnName(resolvedInstance, addonModuleId);
            return (_jsxs("div", { className: "uxpcore_componentrenderer__container", children: [_jsx(DefaultLoader, {}), _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder", children: _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder-content", style: { backgroundImage: `url(${addOns.getThunmbNailImagePath(addonModuleId)})` }, children: _jsxs("div", { className: "uxpcore_componentrenderer__addon-placeholder-details", children: [_jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-name", children: resolvedInstance.name }), _jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-addon-name", children: addOnName }), _jsx("p", { className: "uxpcore_componentrenderer__addon-placeholder-message", children: _jsx("span", { dangerouslySetInnerHTML: { __html: !installed
                                                    ? context.$L('uxp-core.addon.install-and-configure')
                                                    : context.$L('uxp-core.addon.configure')
                                            } }) }), _jsx("div", { className: "uxpcore_componentrenderer__addon-placeholder-actions", children: _jsx(ButtonComponent, { title: !installed ? context.$L('uxp-core.text.install') : context.$L('uxp-core.text.configure'), onClick: () => setInstallInstance(resolvedInstance), leftIcon: !installed ? 'fas cloud-download' : 'fas cog' }) })] }) }) }), _jsx(InstallAddOn, { widget: installInstance, show: installInstance !== null, onInstallationComplete: (w) => { onAddonInstalled === null || onAddonInstalled === void 0 ? void 0 : onAddonInstalled(w); setInstallInstance(null); }, onConfigurationComplete: (w) => { onAddonInstalled === null || onAddonInstalled === void 0 ? void 0 : onAddonInstalled(w); setInstallInstance(null); }, onClose: () => setInstallInstance(null) })] }));
        }
    }
    // Augment the instance with caller-supplied overrides for validation
    const schemaDefaults = (_a = resolvedInstance.defaultProps) !== null && _a !== void 0 ? _a : extractDefaultPropsFromSchema(resolvedInstance);
    const resolvedConfiguredProps = (_b = configuredProps !== null && configuredProps !== void 0 ? configuredProps : resolvedInstance.configuredProps) !== null && _b !== void 0 ? _b : {};
    const resolvedHasConfigured = (_c = hasConfigured !== null && hasConfigured !== void 0 ? hasConfigured : resolvedInstance.hasConfigured) !== null && _c !== void 0 ? _c : Object.keys(resolvedConfiguredProps).length > 0;
    const checkInstance = Object.assign(Object.assign({}, resolvedInstance), { defaultProps: schemaDefaults, configuredProps: resolvedConfiguredProps, hasConfigured: resolvedHasConfigured });
    if (shouldShowPlaceholder(checkInstance)) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(ConfigPlaceholder, { name: resolvedInstance.name, message: configNeededMessage, onConfigure: onConfigureRequest }) }));
    }
    if (forceConfigView) {
        if (hasConfigOptions(resolvedInstance)) {
            return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: _jsx(ConfigPlaceholder, { name: resolvedInstance.name, message: configNeededMessage, onConfigure: onConfigureRequest }) }));
        }
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__no-config", children: _jsx("p", { className: "uxpcore_componentrenderer__no-config-message", children: context.$L('uxp-core.text.no-configuration-required') }) }));
    }
    const comp = resolvedInstance.component;
    if (!comp || !isValidElementType(comp)) {
        return (_jsx("div", { className: "uxpcore_componentrenderer__container uxpcore_componentrenderer__not-found", children: context.$L('uxp-core.text.invalid-component-type') }));
    }
    // Strip React reserved props before spreading to avoid key/ref clashes
    const _d = (additionalProps || {}), { key: _k, ref: _r } = _d, safeAdditionalProps = __rest(_d, ["key", "ref"]);
    const merged = Object.assign(Object.assign(Object.assign({}, getMergedWidgetProps(checkInstance)), safeAdditionalProps), { uxpContext: context, isPreview });
    return (_jsx("div", { className: "uxpcore_componentrenderer__container", children: React.createElement(comp, merged) }));
};
/**
 * @export
 * A component to fetch and render any widget or UI component dynamically.
 */
export const ComponentRenderer = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(ComponentRendererBase, Object.assign({}, props)) })));
export default ComponentRenderer;
